import { useEffect, useState } from 'react';

interface SplashScreenProps {
  onDone: () => void;
}

const FEATURES = [
  { icon: '⚖️', label: 'Juriste référent dédié' },
  { icon: '🛡️', label: 'Conformité RGPD & IA Act' },
  { icon: '📋', label: 'Gouvernance documentaire' },
  { icon: '🤝', label: 'Accompagnement sur mesure' },
];

export default function SplashScreen({ onDone }: SplashScreenProps) {
  const [phase, setPhase] = useState(0);
  const [leaving, setLeaving] = useState(false);

  // Animations d'entrée uniquement — pas d'auto-fermeture
  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 600),
      setTimeout(() => setPhase(2), 1500),
      setTimeout(() => setPhase(3), 2400),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const handleAccess = () => {
    setLeaving(true);
    setTimeout(onDone, 500);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden',
      opacity: leaving ? 0 : 1,
      transition: 'opacity 0.5s ease',
      pointerEvents: leaving ? 'none' : 'auto',
    }}>
      {/* Photo background */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'url(/bg_login.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }} />
      {/* Dark overlay */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,10,20,0.55)' }} />

      {/* Content */}
      <div style={{
        position: 'relative', zIndex: 10,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '0 16px',
      }}>

        {/* Logo */}
        <div style={{
          opacity: phase >= 0 ? 1 : 0,
          transform: phase >= 0 ? 'scale(1) translateY(0)' : 'scale(0.4) translateY(40px)',
          transition: 'all 0.9s cubic-bezier(0.34, 1.56, 0.64, 1)',
          marginBottom: 20,
          filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.5))',
        }}>
          <img
            src="/logo_vaegc.png"
            alt="VÆG Conformité"
            style={{ width: 'clamp(90px, 20vw, 140px)', height: 'clamp(90px, 20vw, 140px)', objectFit: 'contain' } as React.CSSProperties}
          />
        </div>

        {/* Nom */}
        <div style={{
          opacity: phase >= 1 ? 1 : 0,
          transform: phase >= 1 ? 'translateY(0)' : 'translateY(24px)',
          transition: 'all 0.7s ease',
          textAlign: 'center',
          marginBottom: 8,
        }}>
          <h1 style={{
            fontSize: 'clamp(24px, 6vw, 44px)',
            fontWeight: 800,
            color: '#fff',
            letterSpacing: '0.05em',
            margin: 0,
            textShadow: '0 2px 20px rgba(0,0,0,0.7)',
          }}>
            VÆG <span style={{ color: '#29B6D8' }}>CONFORMITÉ</span>
          </h1>
          <div style={{
            height: 2,
            background: 'linear-gradient(90deg, transparent, #29B6D8, #F5A623, transparent)',
            margin: '10px auto 0',
            width: 'clamp(200px, 50vw, 320px)',
            borderRadius: 2,
          }} />
        </div>

        {/* Tagline */}
        <div style={{
          opacity: phase >= 1 ? 1 : 0,
          transform: phase >= 1 ? 'translateY(0)' : 'translateY(16px)',
          transition: 'all 0.6s ease 0.2s',
          textAlign: 'center',
          marginBottom: 32,
        }}>
          <p style={{
            fontSize: 'clamp(14px, 3vw, 20px)',
            fontStyle: 'italic',
            color: 'rgba(255,255,255,0.88)',
            margin: '8px 0 0',
            textShadow: '0 1px 10px rgba(0,0,0,0.6)',
          }}>Votre conformité, notre expertise.</p>
        </div>

        {/* Feature pills */}
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center',
          opacity: phase >= 2 ? 1 : 0,
          transform: phase >= 2 ? 'translateY(0)' : 'translateY(16px)',
          transition: 'all 0.5s ease',
          marginBottom: 'clamp(24px, 5vh, 40px)',
          maxWidth: '90vw',
        }}>
          {FEATURES.map((f, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '8px 16px',
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 30,
              backdropFilter: 'blur(12px)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 500,
              opacity: phase >= 2 ? 1 : 0,
              transform: phase >= 2 ? 'scale(1)' : 'scale(0.85)',
              transition: `all 0.4s ease ${i * 0.08}s`,
              textShadow: '0 1px 6px rgba(0,0,0,0.5)',
            }}>
              <span style={{ fontSize: 15 }}>{f.icon}</span>
              {f.label}
            </div>
          ))}
        </div>

        {/* CTA — se connecter */}
        <div style={{
          opacity: phase >= 3 ? 1 : 0,
          transform: phase >= 3 ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(12px)',
          transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
        }}>
          <button
            onClick={handleAccess}
            style={{
              padding: '14px 48px',
              background: 'linear-gradient(135deg, #29B6D8 0%, #1B2A6B 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 50,
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.05em',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 50px rgba(41,182,216,0.2)',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => { (e.currentTarget.style.transform = 'scale(1.06)'); (e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.5)'); }}
            onMouseLeave={e => { (e.currentTarget.style.transform = 'scale(1)'); (e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.4)'); }}
          >
            Accéder à la connexion →
          </button>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', margin: 0 }}>
            Votre partenaire juridique de confiance
          </p>
        </div>
      </div>
    </div>
  );
}
